/**
 * useRetentionPeriodsMeta - Metadados de período das solicitações de Retenção
 *
 * Busca, em UMA chamada (evita N+1), a data de solicitação e a data de
 * fechamento das tarefas visíveis, permitindo calcular o badge de "Período"
 * na listagem e no Kanban sem alterar as RPCs pesadas de paginação.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-22 v1.1.0 - Também atende o módulo Jurídico (data-base =
 *          data de entrada) e retorna o motivo da conclusão (nome/cor).
 * RELEASE: 2026-08-22 v1.0.0 - Criação inicial
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useRetentionPeriodsMeta');

export interface RetentionTaskMeta {
  task_id: string;
  requested_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  /** v1.1.0 — motivo da conclusão (Retenção/Jurídico) */
  completion_reason_name?: string | null;
  completion_reason_color?: string | null;
  /** v1.2.0 — nº do protocolo (Retenção) exibido no card do Kanban */
  protocol_number?: string | null;
  /** v1.2.0 — nº do processo (Jurídico) */
  process_number?: string | null;
  /** v1.2.0 — advogado responsável (Jurídico) */
  lawyer?: string | null;
  /** v1.2.0 — tipo da ação (Jurídico) */
  action_type?: string | null;
  /** v1.2.0 — prazo processual (Jurídico) */
  deadline_date?: string | null;
  /** v1.2.0 — valor da causa (Jurídico) */
  case_value?: number | null;
}


/**
 * @param taskIds  IDs das tarefas visíveis na tela
 * @param enabled  Só busca quando o módulo corrente é Retenção ou Jurídico
 */
export function useRetentionPeriodsMeta(taskIds: string[], enabled = true) {
  const { currentTenant } = useAuth();

  // Chave estável: ordena os ids para evitar refetch por mudança de ordem
  const sortedIds = useMemo(() => [...taskIds].sort(), [taskIds]);

  const query = useQuery({
    queryKey: ['retention-periods-meta', currentTenant?.id, sortedIds],
    queryFn: async () => {
      if (sortedIds.length === 0) return {} as Record<string, RetentionTaskMeta>;

      const { data, error } = await supabase.rpc('get_tasks_retention_meta', {
        p_task_ids: sortedIds,
        p_tenant_hint: currentTenant?.id || null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar períodos de retenção', { error: error.message });
        throw error;
      }

      const map: Record<string, RetentionTaskMeta> = {};
      ((data || []) as RetentionTaskMeta[]).forEach((row) => {
        map[row.task_id] = row;
      });
      return map;
    },
    enabled: !!currentTenant?.id && enabled && sortedIds.length > 0,
    staleTime: 60_000,
  });

  return {
    metaById: query.data || {},
    isLoading: query.isLoading,
  };
}
